// Rebindable controls (§8.1, §8.2), persisted under `shoveoff.bindings.v1`. Every key action has
// two slots (primary, secondary); every pad action has up to two buttons. Move and aim come from
// WASD/arrows and the sticks; vote cards 1/2/3 reuse the emote1–3 keys during intermission.
// Rebinding (§14.4 "Key rebinding (2 per action), Reset"): `rebindKey` / `rebindPad` assign a code
// to one slot and unbind it wherever else it was (the displaced slot is reported so the UI can say
// "J unbound from Shove"); a code already in the action's other slot swaps the two slots. Escape is
// reserved for Menu/Back and cannot be given to another action.
import { readJson, STORAGE_KEYS, writeJson } from '../profile/storage';

/** Keyboard/mouse actions; every action has two binding slots. */
export type KeyAction =
  | 'up' | 'down' | 'left' | 'right' | 'jump' | 'shove' | 'grab' | 'item' | 'honk'
  | 'emote1' | 'emote2' | 'emote3' | 'emote4' | 'scoreboard' | 'menu' | 'ready' | 'fullscreen' | 'mute';
/** Gamepad button actions (Standard mapping indices). */
export type PadAction = 'jump' | 'shove' | 'grab' | 'item' | 'honk' | 'emote1' | 'emote2' | 'emote3' | 'emote4' | 'scoreboard' | 'menu';

/** `KeyboardEvent.code` ('KeyW', 'Space', 'ArrowUp') or a mouse button 'Mouse0'–'Mouse4'; null = unbound. */
export type KeyBinding = string | null;

export interface Bindings {
  keys: Record<KeyAction, [KeyBinding, KeyBinding]>;
  pad: Record<PadAction, number[]>;
}

export const DEFAULT_BINDINGS: Readonly<Bindings> = {
  keys: {
    up: ['KeyW', 'ArrowUp'],
    down: ['KeyS', 'ArrowDown'],
    left: ['KeyA', 'ArrowLeft'],
    right: ['KeyD', 'ArrowRight'],
    jump: ['Space', null],
    shove: ['Mouse0', 'KeyJ'],
    grab: ['Mouse2', 'KeyK'],
    item: ['KeyE', 'KeyL'],
    honk: ['KeyQ', 'KeyH'],
    emote1: ['Digit1', null],
    emote2: ['Digit2', null],
    emote3: ['Digit3', null],
    emote4: ['Digit4', null],
    scoreboard: ['Tab', null],
    menu: ['Escape', null],
    ready: ['Enter', null],
    fullscreen: ['KeyF', null],
    mute: ['KeyM', null],
  },
  pad: {
    jump: [0],
    shove: [2, 7],
    grab: [1, 6],
    item: [3, 5],
    honk: [4],
    emote1: [12],     // D-pad up: wave
    emote2: [15],     // D-pad right: dance
    emote3: [13],     // D-pad down: laugh
    emote4: [14],     // D-pad left: flex
    scoreboard: [8],
    menu: [9],
  },
};

// ---- Validation and persistence ---------------------------------------------------------------

const KEY_ACTIONS = Object.keys(DEFAULT_BINDINGS.keys) as KeyAction[];
const PAD_ACTIONS = Object.keys(DEFAULT_BINDINGS.pad) as PadAction[];
const KEY_CODE = /^(Mouse[0-4]|[A-Za-z][A-Za-z0-9]{0,31})$/;
const PAD_BUTTONS = 17;

export function cloneBindings(b: Readonly<Bindings>): Bindings {
  const keys = {} as Bindings['keys'];
  for (const a of KEY_ACTIONS) keys[a] = [b.keys[a][0], b.keys[a][1]];
  const pad = {} as Bindings['pad'];
  for (const a of PAD_ACTIONS) pad[a] = [...b.pad[a]];
  return { keys, pad };
}

function keyBinding(v: unknown): KeyBinding {
  return typeof v === 'string' && KEY_CODE.test(v) ? v : null;
}

/** A full, valid Bindings from untrusted JSON; invalid or missing actions keep their defaults. */
export function sanitizeBindings(raw: unknown): Bindings {
  const out = cloneBindings(DEFAULT_BINDINGS);
  if (typeof raw !== 'object' || raw === null) return out;
  const { keys, pad } = raw as { keys?: unknown; pad?: unknown };
  if (typeof keys === 'object' && keys !== null) {
    for (const a of KEY_ACTIONS) {
      const v = (keys as Record<string, unknown>)[a];
      if (Array.isArray(v) && v.length === 2) out.keys[a] = [keyBinding(v[0]), keyBinding(v[1])];
    }
  }
  if (typeof pad === 'object' && pad !== null) {
    for (const a of PAD_ACTIONS) {
      const v = (pad as Record<string, unknown>)[a];
      if (Array.isArray(v)) out.pad[a] = [...new Set(v.filter((n): n is number => Number.isInteger(n) && n >= 0 && n < PAD_BUTTONS))].slice(0, 2);
    }
  }
  return out;
}

export function loadBindings(): Bindings {
  return sanitizeBindings(readJson(STORAGE_KEYS.bindings));
}

export function saveBindings(b: Bindings): void {
  writeJson(STORAGE_KEYS.bindings, b);
}

/** Escape always reaches the menu, whatever the bindings say (it is also the rebinding cancel key). */
export const RESERVED_KEY = 'Escape';

/** Display order and labels for the Settings > Controls rebinding list (§8.1). */
export const KEY_ACTION_LABELS: Readonly<Record<KeyAction, string>> = {
  up: 'Move up', down: 'Move down', left: 'Move left', right: 'Move right', jump: 'Jump', shove: 'Shove / Pebble',
  grab: 'Grab / Throw', item: 'Use item', honk: 'Honk', emote1: 'Emote: Wave', emote2: 'Emote: Dance', emote3: 'Emote: Laugh',
  emote4: 'Emote: Flex', scoreboard: 'Scoreboard (hold)', menu: 'Menu / Pause', ready: 'Ready / Skip replay',
  fullscreen: 'Fullscreen', mute: 'Mute',
};
export const PAD_ACTION_LABELS: Readonly<Record<PadAction, string>> = {
  jump: 'Jump', shove: 'Shove / Pebble', grab: 'Grab / Throw', item: 'Use item', honk: 'Honk', emote1: 'Emote: Wave',
  emote2: 'Emote: Dance', emote3: 'Emote: Laugh', emote4: 'Emote: Flex', scoreboard: 'Scoreboard (hold)', menu: 'Menu / Pause',
};
export const KEY_ACTION_ORDER: readonly KeyAction[] = KEY_ACTIONS;
export const PAD_ACTION_ORDER: readonly PadAction[] = PAD_ACTIONS;

export interface RebindResult {
  bindings: Bindings;
  /** The slot that lost this code, if it was bound elsewhere. */
  displaced: { action: KeyAction; slot: 0 | 1 } | null;
  /** False when the code is not allowed for this action (bad code, or Escape on a non-menu action). */
  ok: boolean;
}

export interface PadRebindResult {
  bindings: Bindings;
  displaced: { action: PadAction; slot: number } | null;
  ok: boolean;
}

export function isValidKeyCode(code: string): boolean {
  return KEY_CODE.test(code);
}

/** Binds `code` to `action` slot `slot` (0 primary, 1 secondary); `null` clears the slot. */
export function rebindKey(b: Readonly<Bindings>, action: KeyAction, slot: 0 | 1, code: string | null): RebindResult {
  const out = cloneBindings(b);
  if (code !== null && (!isValidKeyCode(code) || (code === RESERVED_KEY && action !== 'menu'))) return { bindings: out, displaced: null, ok: false };
  let displaced: RebindResult['displaced'] = null;
  const other = (1 - slot) as 0 | 1;
  if (code !== null && out.keys[action][other] === code) {
    // Already this action's other slot: swap the two slots.
    out.keys[action][other] = out.keys[action][slot];
    out.keys[action][slot] = code;
    return { bindings: out, displaced: null, ok: true };
  }
  if (code !== null) {
    for (const a of KEY_ACTIONS) {
      for (const s of [0, 1] as const) {
        if (out.keys[a][s] === code && !(a === action && s === slot)) {
          out.keys[a][s] = null;
          displaced = { action: a, slot: s };
        }
      }
    }
  }
  out.keys[action][slot] = code;
  return { bindings: out, displaced, ok: true };
}

/** Binds pad button `button` to `action` slot `slot` (0 or 1); `null` clears the slot. */
export function rebindPad(b: Readonly<Bindings>, action: PadAction, slot: 0 | 1, button: number | null): PadRebindResult {
  const out = cloneBindings(b);
  if (button !== null && !(Number.isInteger(button) && button >= 0 && button < PAD_BUTTONS)) return { bindings: out, displaced: null, ok: false };
  let displaced: PadRebindResult['displaced'] = null;
  const own = button === null ? -1 : out.pad[action].indexOf(button);
  if (own >= 0) {
    // Already bound to this action: swap into the slot (or keep it where it is).
    const list = out.pad[action];
    if (slot < list.length && own !== slot) [list[own], list[slot]] = [list[slot], list[own]];
    return { bindings: out, displaced: null, ok: true };
  }
  if (button !== null) {
    for (const a of PAD_ACTIONS) {
      if (a === action) continue;
      const i = out.pad[a].indexOf(button);
      if (i >= 0) {
        out.pad[a].splice(i, 1);
        displaced = { action: a, slot: i };
      }
    }
  }
  const list = out.pad[action].slice(0, 2);
  if (button === null) list.splice(slot, 1);
  else if (slot < list.length) list[slot] = button;
  else list.push(button);
  out.pad[action] = [...new Set(list)];
  return { bindings: out, displaced, ok: true };
}

/** Pad buttons bound to `action`, with the Swap Jump/Grab setting applied (A ↔ B, §14.4). */
export function padButtonsFor(b: Readonly<Bindings>, action: PadAction, swapJumpGrab: boolean): readonly number[] {
  const list = b.pad[action];
  if (!swapJumpGrab) return list;
  return list.map(swapAB);
}

/** A ↔ B (buttons 0 and 1) for the Swap Jump/Grab setting. */
export function swapAB(btn: number): number {
  return btn === 0 ? 1 : btn === 1 ? 0 : btn;
}

/** The key action bound to a `KeyboardEvent.code` or mouse button name, or null. */
export function keyActionFor(b: Readonly<Bindings>, code: string): KeyAction | null {
  for (const a of KEY_ACTIONS) {
    const [p, s] = b.keys[a];
    if (p === code || s === code) return a;
  }
  return code === RESERVED_KEY ? 'menu' : null;
}
